import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Popup } from "react-map-gl/mapbox";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { DateTime } from "luxon";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { rain } from "@/endpoints/rain/rain";
import { TIME_PERIODS, type TimePeriodsValue } from "@/config/date";
import { getDateRangeFromTimePeriod } from "@/components/TimePeriod/helper";
import { POPUP_ANIM, RainContent, type RainContentData } from "./RainContent";

const DAILY_AGGREGATE_PERIODS: TimePeriodsValue[] = ["last90d", "last1y", "last10y"];
const FALLBACK_PERIOD: TimePeriodsValue = "last30d";

// The rain API rejects ISO timestamps with a non-UTC offset (e.g. "+02:00")
// with a 400 — emit UTC `Z` to match the original page-map behaviour.
const timePeriodToISO = (period: TimePeriodsValue): { start: string; end: string } => {
	const range = getDateRangeFromTimePeriod(period) ?? getDateRangeFromTimePeriod(FALLBACK_PERIOD);
	if (!range?.from || !range.to) {
		const now = DateTime.utc().toISO() ?? "";
		return { start: now, end: now };
	}
	return {
		start: DateTime.fromJSDate(range.from).toUTC().toISO() ?? "",
		end: DateTime.fromJSDate(range.to).toUTC().toISO() ?? "",
	};
};

export interface RainGaugeStation {
	id: string;
	name: string;
	provider: string;
	lng: number;
	lat: number;
}

interface RainGaugePopupProps {
	station: RainGaugeStation;
	timePeriod: TimePeriodsValue;
	onClose: () => void;
}

export const RainGaugePopup: React.FC<RainGaugePopupProps> = observer(({ station, timePeriod, onClose }) => {
	const timeseriesApi = useAsyncStateStore(rain.getStationTimeseries);
	const statisticsApi = useAsyncStateStore(rain.getStationStatistics);
	const isDailyView = DAILY_AGGREGATE_PERIODS.includes(timePeriod);

	useEffect(() => {
		const isoRange = timePeriodToISO(timePeriod);
		if (isDailyView) {
			statisticsApi.fetch(station.id, {
				...isoRange,
				rollingWindowMinutes: 1440,
				rollingAggregation: "sum",
			});
		} else {
			timeseriesApi.fetch(station.id, {
				...isoRange,
				samples: 200,
				gapFill: true,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [station.id, timePeriod]);

	const activeApi = isDailyView ? statisticsApi : timeseriesApi;

	const data: RainContentData | null = isDailyView
		? statisticsApi.data
			? {
					mode: "daily",
					rollingWindows: statisticsApi.data.rollingWindows ?? [],
					aggregates: statisticsApi.data.aggregates ?? null,
					timePeriodLabel: TIME_PERIODS.find(period => period.value === timePeriod)?.label,
				}
			: null
		: timeseriesApi.data
			? {
					mode: "raw",
					timeseries: timeseriesApi.data.data ?? [],
					timePeriodLabel: TIME_PERIODS.find(period => period.value === timePeriod)?.label,
				}
			: null;

	return (
		<Popup
			className="popup-rain"
			longitude={station.lng}
			latitude={station.lat}
			closeButton={false}
			closeOnClick={false}
			closeOnMove={false}
			anchor="bottom"
			maxWidth="340px"
			onClose={onClose}
		>
			<motion.div className="min-w-[300px]" variants={POPUP_ANIM} initial="initial" animate="animate" exit="exit">
				<div className="flex items-start justify-between gap-3 px-3.5 pt-3 pb-2">
					<div className="min-w-0">
						<h3 className="text-sm font-semibold text-foreground leading-tight truncate">{station.name}</h3>
						<div className="flex items-center gap-1.5 mt-1">
							<span className="px-1 py-0 text-[8px] leading-4 font-medium uppercase tracking-wide rounded bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800">
								{station.provider}
							</span>
							<span className="text-[10px] text-muted-foreground">
								{station.lat.toFixed(4)}, {station.lng.toFixed(4)}
							</span>
						</div>
					</div>
					<button
						className="p-1 rounded-md hover:bg-muted transition-colors flex-shrink-0 -mr-1 -mt-0.5"
						onClick={onClose}
					>
						<X className="h-3.5 w-3.5 text-muted-foreground" />
					</button>
				</div>
				<div className="border-t border-border mx-3.5" />
				<div className="px-3.5 pt-2.5 pb-3">
					<RainContent loading={activeApi.loading} data={data} />
				</div>
			</motion.div>
		</Popup>
	);
});
